import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { UploadIcon, XIcon } from "lucide-react";
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Heading,
    Input,
    Text,
} from "@athena/ui";
import type { CreateClientFlagEntry, CreateClientSkipReason } from "@athena/common/graphql/generated/schema";
import { CREATE_CLIENTS_MAX_ROWS } from "@athena/common/utils/clientImport";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CheckClientsDocument, CreateClientsDocument } from "@/graphql/clients.generated";
import { ALUMNI_ROOT } from "@/lib/adminPilot";
import {
    CLIENT_IMPORT_TEMPLATE,
    checkClientImportRows,
    parseClientImportCsv,
    type ClientImportFile,
    type ClientImportRow,
    type ClientImportRowProblem,
} from "@/lib/clientImportCsv";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useSendWelcomeEmails } from "./helpers/useSendWelcomeEmails";

/** Fields a flag can name that read better spelled out; anything else is shown as the API names it. */
const FLAGGED_FIELDS = [
    "phoneNumber",
    "emergencyContactPhone",
    "dateOfBirth",
    "dischargeDate",
    "soberDate",
] as const;
type FlaggedField = (typeof FLAGGED_FIELDS)[number];
const isFlaggedField = (field: string): field is FlaggedField => (FLAGGED_FIELDS as readonly string[]).includes(field);

/** The required fields, which a coordinator can correct in place on a row that has a problem. */
type EditableField = "mrNumber" | "email";

/** The field each row problem is about, so its input is the one marked. */
const PROBLEM_FIELD: Record<ClientImportRowProblem, EditableField> = {
    MISSING_MR_NUMBER: "mrNumber",
    INVALID_MR_NUMBER: "mrNumber",
    DUPLICATE_MR_NUMBER_IN_FILE: "mrNumber",
    MISSING_EMAIL: "email",
    INVALID_EMAIL: "email",
    DUPLICATE_EMAIL_IN_FILE: "email",
};

/** The same for the API's answers. Those the browser catches first never come back from the check. */
const SKIP_FIELD: Partial<Record<CreateClientSkipReason, EditableField>> = {
    DUPLICATE_MR_NUMBER: "mrNumber",
    ALREADY_A_CLIENT: "email",
    EXISTING_ACCOUNT: "email",
};

/** How long typing in a row settles before the rows are checked against the system again. */
const CHECK_DELAY_MS = 400;

/** What the API's check said about one row that the browser found nothing wrong with. */
type RowCheck =
    | { reason: CreateClientSkipReason }
    | { flags: Pick<CreateClientFlagEntry, "flag" | "field" | "value">[] };

/**
 * Adds alumni from a CSV (ATH-267): each row becomes an account, a client-org membership and a
 * profile under its KIPU MR number, through `createClients`.
 *
 * Every row is checked before anything is added. The browser checks the file itself
 * (`parseClientImportCsv`); the rows that pass go to `checkClients`, which answers as `createClients`
 * would — already a client, a staff account, a value it would leave empty — without adding anyone.
 * A row with a problem is corrected in place, or removed with its ×, and nothing can be added until
 * every row left is ready. Then the import adds them all, a dialog says so, and the coordinator is
 * back in the directory. Nobody is emailed: inviting is its own step.
 */
export function ClientImportPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fileInput = useRef<HTMLInputElement>(null);
    const [
        fileName,
        setFileName,
    ] = useState<string | undefined>(undefined);
    const [
        file,
        setFile,
    ] = useState<ClientImportFile | undefined>(undefined);
    const [
        confirming,
        setConfirming,
    ] = useState<boolean>(false);
    const [
        failed,
        setFailed,
    ] = useState<boolean>(false);
    /** Corrections typed into rows with a problem, by line, as typed. Cleared when a file is chosen. */
    const [
        edits,
        setEdits,
    ] = useState<Map<number, Partial<Record<EditableField, string>>>>(new Map());
    /** Lines taken out of this import with their ×. Cleared when a file is chosen. */
    const [
        removed,
        setRemoved,
    ] = useState<Set<number>>(new Set());
    /** What the import did, once it has run. Opens the success dialog. */
    const [
        result,
        setResult,
    ] = useState<{ added: number; notAdded: number; clientIds: string[] } | undefined>(undefined);
    const { sendWelcomeEmails, sending, confirmDialog } = useSendWelcomeEmails();
    const [
        createClients,
        { loading: adding },
    ] = useMutation(CreateClientsDocument, {
        // The directory reads `Query.clients` whole; drop it so the new clients are there on return.
        update: (cache) => {
            cache.evict({ fieldName: "clients" });
            cache.gc();
        },
    });

    const parsedRows = file?.rows ?? [];
    // Re-checked as a whole, since correcting or removing one row can make or clear a duplicate elsewhere.
    const rows = checkClientImportRows(
        parsedRows
            .filter((row) => !removed.has(row.line))
            .map((row) => ({ line: row.line, input: { ...row.input, ...edits.get(row.line) } })),
    );
    const problemFree = rows.filter((row) => !row.problem);

    // The rows the browser passed go to the API, once typing settles. Compared as text, since the
    // rows are rebuilt on every render.
    const toCheck = JSON.stringify(problemFree.map(({ line, input }) => ({ line, input })));
    const settled = useDebouncedValue(toCheck, CHECK_DELAY_MS);
    const checked = JSON.parse(settled) as Pick<ClientImportRow, "line" | "input">[];
    const {
        data: checkData,
        loading: checkLoading,
        error: checkError,
    } = useQuery(CheckClientsDocument, {
        variables: { clients: checked.map((row) => row.input) },
        skip: checked.length === 0,
        // Who is already a client changes under us; a cached answer could be someone else's import ago.
        fetchPolicy: "network-only",
    });
    const checking = toCheck !== settled || checkLoading;

    /**
     * Each line's latest answer, with the row it was for. Kept while the row is checked again, so a
     * keystroke doesn't blank the counts and the message above the table until the answer is back.
     */
    const [
        checks,
        setChecks,
    ] = useState<Map<number, { input: string; check: RowCheck }>>(new Map());
    useEffect(() => {
        if (!checkData || checkLoading) return;
        setChecks((current) => {
            const next = new Map(current);
            // The API indexes the rows it was sent.
            const answer = (index: number, check: RowCheck) => {
                const row = checked[index];
                if (row) next.set(row.line, { input: JSON.stringify(row.input), check });
            };
            for (const ready of checkData.checkClients.ready) answer(ready.index, { flags: ready.flags });
            for (const skipped of checkData.checkClients.skipped) answer(skipped.index, { reason: skipped.reason });
            return next;
        });
        // Not on `checked`: it is rebuilt every render, and read here only for the answer that just came back.
    }, [
        checkData,
        checkLoading,
    ]);
    /** The answer for the row as it stands now; undefined while it is being checked. */
    const currentCheck = (row: ClientImportRow) => {
        const entry = checks.get(row.line);
        return entry?.input === JSON.stringify(row.input) ? entry.check : undefined;
    };
    /** Why the API last refused this line, even if it has been edited since. */
    const lastSkipReason = (row: ClientImportRow) => {
        const check = checks.get(row.line)?.check;
        return check && "reason" in check ? check.reason : undefined;
    };

    // A row stays editable once it had a problem or was edited, so its inputs don't vanish the moment it is fixed.
    const editable = (row: ClientImportRow) =>
        Boolean(
            parsedRows.find((parsed) => parsed.line === row.line)?.problem ||
            row.problem ||
            lastSkipReason(row) ||
            edits.has(row.line),
        );
    // A row being fixed counts as needing it until its new answer is back, so the message stays put.
    const needsFixing = (row: ClientImportRow) => {
        if (row.problem) return true;
        const check = currentCheck(row);
        return check ? "reason" in check : editable(row);
    };

    const needFixing = rows.filter(needsFixing).length;
    const readyCount = rows.length - needFixing;
    const canAdd = rows.length > 0 && needFixing === 0 && !checking && !checkError && !adding;

    const edit = (line: number, field: EditableField, value: string) =>
        setEdits((current) => new Map(current).set(line, { ...current.get(line), [field]: value }));

    const remove = (line: number) => setRemoved((current) => new Set(current).add(line));

    const readFile = async (chosen: File) => {
        setFileName(chosen.name);
        setFile(parseClientImportCsv(await chosen.text()));
        setEdits(new Map());
        setRemoved(new Set());
        setChecks(new Map());
        setFailed(false);
        // Without this the same file can't be chosen again after a fix: the input fires no change
        // event when its value is unchanged.
        if (fileInput.current) fileInput.current.value = "";
    };

    const downloadTemplate = () => {
        const url = URL.createObjectURL(new Blob([CLIENT_IMPORT_TEMPLATE], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "client-import-template.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    const add = async () => {
        setFailed(false);
        try {
            const { data } = await createClients({ variables: { clients: rows.map((row) => row.input) } });
            const created = data?.createClients;
            if (!created) throw new Error("No result");
            // Every row was checked first, so a skip here is someone else's import in between.
            setResult({
                added: created.created.length + created.linked.length,
                notAdded: created.skipped.length,
                clientIds: [
                    ...created.created,
                    ...created.linked,
                ].map((client) => client.clientId),
            });
        } catch {
            setFailed(true);
        } finally {
            setConfirming(false);
        }
    };

    const fileProblem = (() => {
        if (!file?.problem) return undefined;
        if (file.problem === "MISSING_COLUMNS") {
            const columns = (file.missingColumns ?? [])
                .map((column) => t(`admin.alumni.import.requiredColumn.${column}`))
                .join(" or ");
            return t("admin.alumni.import.fileProblem.MISSING_COLUMNS", { columns });
        }
        if (file.problem === "TOO_MANY_ROWS") {
            return t("admin.alumni.import.fileProblem.TOO_MANY_ROWS", { max: CREATE_CLIENTS_MAX_ROWS });
        }
        return t("admin.alumni.import.fileProblem.NO_ROWS");
    })();

    const flagText = (flag: Pick<CreateClientFlagEntry, "flag" | "field" | "value">) =>
        t(`admin.alumni.import.flag.${flag.flag}`, {
            value: flag.value,
            field: isFlaggedField(flag.field) ? t(`admin.alumni.import.fieldName.${flag.field}`) : flag.field,
        });

    const requiredField = (row: ClientImportRow, field: EditableField) => {
        if (!editable(row)) return row.input[field] || "—";
        const reason = lastSkipReason(row);
        const invalid =
            (row.problem !== undefined && PROBLEM_FIELD[row.problem] === field) ||
            (reason !== undefined && SKIP_FIELD[reason] === field);
        return (
            <Input
                value={
                    edits.get(row.line)?.[field] ??
                    parsedRows.find((parsed) => parsed.line === row.line)?.input[field] ??
                    ""
                }
                onChange={(event) => edit(row.line, field, event.target.value)}
                aria-label={t(`admin.alumni.import.edit.${field}`, { line: row.line })}
                aria-invalid={invalid}
                // Pulled left by its own padding, so its text lines up with the plain values above and below.
                className="aria-invalid:border-destructive -ml-2 h-8 px-2"
            />
        );
    };

    const status = (row: ClientImportRow) => {
        if (row.problem) {
            return (
                <Text size="sm" className="text-destructive">
                    {t(`admin.alumni.import.rowProblem.${row.problem}`)}
                </Text>
            );
        }
        const check = currentCheck(row);
        if (!check) {
            return (
                <Text size="sm" muted>
                    {checkError ? "—" : t("admin.alumni.import.status.checking")}
                </Text>
            );
        }
        if ("reason" in check) {
            return (
                <Text size="sm" className="text-destructive">
                    {t(`admin.alumni.import.skipReason.${check.reason}`)}
                </Text>
            );
        }
        return (
            <div className="flex flex-col gap-1">
                <Text size="sm" muted>
                    {t("admin.alumni.import.status.ready")}
                </Text>
                {check.flags.map((flag) => (
                    <Text key={flag.field} size="sm" muted>
                        {flagText(flag)}
                    </Text>
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Heading level={3} as="h1">
                        {t("admin.alumni.import.title")}
                    </Heading>
                    <Text size="sm" muted className="mt-1">
                        {t("admin.alumni.import.description")}
                    </Text>
                </div>
                <Button variant="outline" size="sm" className="w-auto" onClick={() => navigate(ALUMNI_ROOT)}>
                    {t("admin.alumni.import.back")}
                </Button>
            </header>

            <div className="bg-muted/50 border-border flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-dashed px-5 py-4">
                <div className="flex items-center gap-4">
                    <div className="border-border bg-background flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border shadow-sm">
                        <UploadIcon className="text-muted-foreground h-5 w-5" aria-hidden />
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <Text weight="semibold">{fileName ?? t("admin.alumni.import.noFile")}</Text>
                        <Text size="sm" muted>
                            {t("admin.alumni.import.columns")}{" "}
                            <button
                                type="button"
                                onClick={downloadTemplate}
                                className="text-text-link cursor-pointer font-bold hover:underline"
                            >
                                {t("admin.alumni.import.downloadTemplate")}
                            </button>
                        </Text>
                    </div>
                </div>
                <Button size="sm" className="w-auto" onClick={() => fileInput.current?.click()}>
                    {fileName ? t("admin.alumni.import.chooseAnother") : t("admin.alumni.import.chooseFile")}
                </Button>
                <input
                    ref={fileInput}
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    aria-label={t("admin.alumni.import.chooseFile")}
                    onChange={(event) => {
                        const chosen = event.target.files?.[0];
                        if (chosen) void readFile(chosen);
                    }}
                />
            </div>

            {fileProblem ? (
                <Text size="sm" weight="semibold" className="text-destructive" role="alert">
                    {fileProblem}
                </Text>
            ) : null}
            {file && file.ignoredColumns.length > 0 ? (
                <Text size="sm" weight="semibold">
                    {t("admin.alumni.import.ignoredColumns", { columns: file.ignoredColumns.join(", ") })}
                </Text>
            ) : null}
            {/* Says why Add is off. No retry button: editing a row checks again, and so does choosing the file again. */}
            {checkError ? (
                <Text size="sm" weight="semibold" className="text-destructive" role="alert">
                    {t("admin.alumni.import.checkFailed")}
                </Text>
            ) : null}
            {failed ? (
                <Text size="sm" weight="semibold" className="text-destructive" role="alert">
                    {t("admin.alumni.import.failed")}
                </Text>
            ) : null}

            {rows.length > 0 ? (
                <>
                    <div className="flex flex-wrap items-center gap-3">
                        <Text size="sm" weight="semibold">
                            {t("admin.alumni.import.ready", { count: readyCount })}
                        </Text>
                        {needFixing > 0 ? (
                            <>
                                <Text size="sm" weight="semibold" className="text-destructive">
                                    {t("admin.alumni.import.needFixing", { count: needFixing })}
                                </Text>
                                <Text size="sm" className="text-destructive basis-full">
                                    {t("admin.alumni.import.fixInline")}
                                </Text>
                            </>
                        ) : null}
                        <Button
                            size="sm"
                            className="ml-auto w-auto"
                            disabled={!canAdd}
                            onClick={() => setConfirming(true)}
                        >
                            {t("admin.alumni.import.add", { count: rows.length })}
                        </Button>
                    </div>

                    <div className="border-border overflow-x-auto rounded-lg border">
                        {/* Fixed columns, so a status changing length doesn't reflow the table under the cursor. */}
                        <table className="w-full min-w-4xl table-fixed text-sm">
                            <thead className="bg-muted/50 text-muted-foreground text-xs">
                                <tr>
                                    <th className="w-14 px-3 py-2 text-left font-medium">
                                        {t("admin.alumni.import.columnLine")}
                                    </th>
                                    <th className="w-52 px-3 py-2 text-left font-medium">
                                        {t("admin.alumni.import.columnMrNumber")}
                                    </th>
                                    <th className="w-44 px-3 py-2 text-left font-medium">
                                        {t("admin.alumni.import.columnName")}
                                    </th>
                                    <th className="w-72 px-3 py-2 text-left font-medium">
                                        {t("admin.alumni.import.columnEmail")}
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium">
                                        {t("admin.alumni.import.columnStatus")}
                                    </th>
                                    <th className="w-12 px-3 py-2">
                                        <span className="sr-only">{t("admin.alumni.import.columnRemove")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.line} className="border-border border-t">
                                        <td className="text-muted-foreground px-3 py-2">{row.line}</td>
                                        <td className="px-3 py-2">{requiredField(row, "mrNumber")}</td>
                                        <td className="px-3 py-2">
                                            {[
                                                row.input.firstName,
                                                row.input.lastName,
                                            ]
                                                .filter(Boolean)
                                                .join(" ") || "—"}
                                        </td>
                                        <td className="px-3 py-2 break-words">{requiredField(row, "email")}</td>
                                        <td className="px-3 py-2">{status(row)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                disabled={adding}
                                                onClick={() => remove(row.line)}
                                                aria-label={t("admin.alumni.import.remove", { line: row.line })}
                                            >
                                                <XIcon className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}

            <ConfirmDialog
                open={confirming}
                onOpenChange={setConfirming}
                title={t("admin.alumni.import.confirmTitle")}
                description={t("admin.alumni.import.confirmDescription", { count: rows.length })}
                confirmLabel={t("admin.alumni.import.add", { count: rows.length })}
                loadingLabel={t("admin.alumni.import.adding")}
                loading={adding}
                onConfirm={add}
            />

            <Dialog
                open={result !== undefined}
                onOpenChange={(open) => {
                    if (!open) navigate(ALUMNI_ROOT);
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("admin.alumni.import.successTitle")}</DialogTitle>
                        <DialogDescription>
                            {[
                                t("admin.alumni.import.successDescription", { count: result?.added ?? 0 }),
                                result?.notAdded
                                    ? t("admin.alumni.import.successNotAdded", { count: result.notAdded })
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col gap-2 sm:flex-col">
                        {/* The dialog already says how many were added: the button repeats it (ATH-244). */}
                        {result?.clientIds.length ? (
                            <Button
                                className="max-w-none"
                                disabled={sending}
                                onClick={() => void sendWelcomeEmails(result.clientIds, () => navigate(ALUMNI_ROOT))}
                            >
                                {sending
                                    ? t("admin.alumni.welcome.sending")
                                    : t("admin.alumni.welcome.sendToImported", { count: result.clientIds.length })}
                            </Button>
                        ) : null}
                        <Button
                            variant={result?.clientIds.length ? "outline" : "default"}
                            className="max-w-none"
                            onClick={() => navigate(ALUMNI_ROOT)}
                        >
                            {t("admin.alumni.import.back")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {confirmDialog}
        </div>
    );
}
